import { FilterDataValue } from '@sancare/typescript-commons/src/types/filters'

import { Gender } from '@/i18n'
import { FilterData, GroupOption, LocalizedOption } from '@/store/modules/filters/types'
import { CriterionCondition } from '@/store/modules/searches/types'
import { CountryEnum, PmsiTypeEnum } from '@/store/modules/settings/types'

import FiltersData from './FiltersData'

export function makeRangeLabel(
  t: (key: string, params?: Record<string, unknown>) => string,
  lowValue: string,
  highValue: string,
  suffix: string = '',
  gender: Gender = Gender.Masculine
) {
  if(highValue[0] === '>' &&  lowValue[0] === '<') {
    return t('range.allValues')
  }

  if (!highValue || highValue[0] === '>') {
    return t(`range.greaterOrEqual.${gender}`, { value: lowValue.replace('>', ''), suffix })
  }

  if (!lowValue || lowValue[0] === '<') {
    return t(`range.lessOrEqual.${gender}`, { value: highValue.replace('<', ''), suffix })
  }

  if(lowValue === highValue) {
    return t(`range.equal.${gender}`, { value: lowValue, suffix })
  }

  return t('range.rangeBetween', { lowValue, highValue, suffix })
}

export const createMulticheckOptions = (
  object: Record<string | number, string>,
  convertValueToNumber: boolean = false,
  icon?: Record<string, string>
) => {
  return Object.entries(object).map(([key, text]) => ({
    value: convertValueToNumber ? Number(key) : key,
    label: text + (icon ? ` ${icon[key]}` : '')
  }))
}

export function optionToValue(option: string | LocalizedOption | GroupOption): string {
  if (typeof option === 'string') return option
  return option.value
}

export function getApplicableSubFilters(
  filterData: FilterData,
  country: CountryEnum,
  pmsiType: PmsiTypeEnum
) {
  return (filterData.subFilters ?? []).filter((subFilter) => {
    if (subFilter.country && !subFilter.country.includes(country)) return false
    if (subFilter.pmsiTypes && !subFilter.pmsiTypes.includes(pmsiType)) return false
    return true
  })
}

export function findFilterEntry(criterionType: string) {
  const lowerType = criterionType.toLowerCase()

  /*
  * We sort filters by the length of their static format text (placeholders removed)
  * before calling `find()` so that more specific formats are matched first.
  * Without this, a generic format could match earlier and hide a more precise one.
  */
  return Object.entries(FiltersData)
    .sort(
      ([, a], [, b]) =>
        (b.format?.replace(/\{[^}]+}/g, '').length ?? 0)
        - (a.format?.replace(/\{[^}]+}/g, '').length ?? 0)
    )
    .find(([, filter]) => {
      if (filter.criterionTypes?.some((t) =>
        t.toLowerCase() === lowerType
      )) {
        return true
      }

      if (!filter.format) return false

      const base = filter.format
        .replace(/\{[^}]+}/g, '')
        .toLowerCase()

      return lowerType.includes(base)
    })
}

export function extractSubFilters(
  filterData: FilterData,
  criterionType: string,
  country: CountryEnum,
  pmsiType: PmsiTypeEnum
): Record<string, string> {
  if (!filterData.format) return {}

  let remaining = criterionType.toLowerCase()
  const base = filterData.format
    .replace(/\{[^}]+}/g, '')
    .toLowerCase()

  remaining = remaining.replace(base, '')

  const subFiltersValues: Record<string, string> = {}

  const subFilters = getApplicableSubFilters(filterData, country, pmsiType)

  subFilters.forEach((subFilter) => {
    const options: Array<string | LocalizedOption | GroupOption> = subFilter.options

    const match = options.find((opt) => {
      const value = typeof opt === 'string' ? opt : opt.value
      return remaining.startsWith(value.toLowerCase())
    })

    if (match) {
      const value = typeof match === 'string' ? match : match.value
      subFiltersValues[subFilter.name] = value
      remaining = remaining.slice(value.length)
    } else if (subFilter.default) {
      subFiltersValues[subFilter.name] = subFilter.default
    }
  })

  return subFiltersValues
}

export function resolveCriterionToFilter(
  criterion: CriterionCondition,
  country: CountryEnum,
  pmsiType: PmsiTypeEnum
): {
  filterName: string
  filterData: FilterData
  filterValue: FilterDataValue
} | null {
  const [criterionType, criterionDocumentCategory] = (criterion.type ?? '').split('__')

  const entry = findFilterEntry(criterionType)
  if (!entry) return null

  const [filterName, filterData] = entry

  return {
    filterName,
    filterData,
    filterValue: {
      value: criterion.value || criterion.rawContent,
      subFilters: extractSubFilters(filterData, criterionType, country, pmsiType),
      documentCategory: criterionDocumentCategory ?? null,
    },
  }
}
